import type { WorkbenchCard, WorkbenchColumn } from '@mybusinessmate/schema';
import type { TagTone } from '@mybusinessmate/ui-tokens';
import { clientStatus } from './task-display';

/** The five lanes of Figma A05.7, in board order: a name, and the line under it. */
export const LANES: { id: WorkbenchColumn; title: string; hint: string; tone: TagTone }[] = [
  { id: 'todo', title: 'To do', hint: 'Assigned, not started', tone: 'grey' },
  { id: 'in_progress', title: 'In progress', hint: 'Move the slider as you go', tone: 'blue' },
  { id: 'changes_requested', title: 'Changes requested', hint: 'From your lead or the client', tone: 'amber' },
  { id: 'ready_for_qa', title: 'Waiting for QA', hint: 'Submitted · hidden from client', tone: 'indigo' },
  { id: 'with_client', title: 'With client', hint: 'Lead approved · client reviewing', tone: 'green' },
];

export const laneOf = (id: WorkbenchColumn) => LANES.find((lane) => lane.id === id)!;

/** The pill beside a card's type: a dot and a word. Never red (Figma A05.7). */
export const PRIORITY_KEY = { High: 'high', Medium: 'medium', Low: 'low' } as const;

/** Whether the person's gauge is full: no new work comes to them until a slot frees. */
export const atCapacity = (active: number, max: number) => max > 0 && active >= max;

// --- Dates --------------------------------------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const dayFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** A calendar day as a number of days since 1970, so two days subtract cleanly. Deadlines are UTC midnights. */
const dayNumber = (iso: string) => Math.floor(new Date(iso).getTime() / DAY_MS);

/** Today's calendar date as the viewer reads it on their own clock (not shifted to UTC). */
const todayNumber = (now: Date) => Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS);

/** "Fri 24 Oct" (the design writes "Sep", not "Sept"). */
export const dayText = (iso: string) => dayFormat.format(new Date(iso)).replace(/\bSept\b/, 'Sep');

export type DueWhen = 'overdue' | 'today' | 'tomorrow' | 'later' | 'undated';

export function dueWhen(deadline: string | null, now: Date = new Date()): DueWhen {
  if (!deadline) return 'undated';
  const diff = dayNumber(deadline) - todayNumber(now);
  return diff < 0 ? 'overdue' : diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : 'later';
}

/** "Review due today" and its kin; the amber ones (`urgent`) are what needs doing now. */
export function dueText(deadline: string | null, now: Date = new Date()): { text: string; urgent: boolean } {
  switch (dueWhen(deadline, now)) {
    case 'undated':
      return { text: 'No review date', urgent: false };
    case 'overdue':
      return { text: 'Review overdue', urgent: true };
    case 'today':
      return { text: 'Review due today', urgent: true };
    case 'tomorrow':
      return { text: 'Review due tomorrow', urgent: true };
    default:
      return { text: `Review due ${dayText(deadline!)}`, urgent: false };
  }
}

/** "40 min", "2 h", "1 d": how long ago, in the design's short form. */
export function ago(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const minutes = Math.max(1, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} h` : `${Math.round(hours / 24)} d`;
}

/** "3 h ago" for a note's byline. */
export const agoText = (iso: string | null, now: Date = new Date()) => (iso ? `${ago(iso, now)} ago` : '');

// --- What a card says ----------------------------------------------------------------------------------------------

/** The small line under a card's title: the client, and for a fix what it changes. */
export function cardSubtitle(card: WorkbenchCard): string {
  const who = card.businessName ?? card.projectName;
  return card.fix ? `${who} · draft fix to live v${card.fix.liveVersion}` : who;
}

/** The version pill: "v2", or "fix" for a proposed fix to a live version. Nothing before the first version. */
export function versionPill(card: WorkbenchCard): string | null {
  if (card.fix) return 'fix';
  return card.version ? `v${card.version.number}` : null;
}

/** The label above a sent-back note: "CLIENT · REVISION 2 OF 2", or the lead's name. */
export function noteLabel(card: WorkbenchCard): string | null {
  const back = card.sentBack;
  if (!back) return null;
  if (back.by === 'client') {
    return back.revision && back.revision.of > 0
      ? `Client · revision ${back.revision.number} of ${back.revision.of}`
      : 'Client';
  }
  return back.byName ? `Lead · ${back.byName}` : 'Lead';
}

/** Who a card is with, and for how long: the line a card in QA or with the client shows instead of a due date. */
export function waitingLine(card: WorkbenchCard, viewerId: string | undefined, now: Date = new Date()): string | null {
  if (card.column === 'with_client') return `Client reviewing · ${ago(card.publishedAt, now)}`;
  if (card.column === 'ready_for_qa') {
    const since = card.fix?.sentAt ?? card.version?.sentAt ?? null;
    const lead = !card.lead ? 'a lead' : card.lead.id === viewerId ? 'you' : card.lead.name;
    return `With ${lead}${since ? ` · ${ago(since, now)}` : ''}`;
  }
  return null;
}

/** The lock line under a card that is in QA: what the client still sees meanwhile. */
export function hiddenLine(card: WorkbenchCard): string | null {
  if (card.column !== 'ready_for_qa') return null;
  return card.fix ? 'Client still sees the live version' : `Client sees “${clientStatus(card.status).label}”`;
}

export const unitsText = (card: WorkbenchCard) => (card.fix ? '—' : `${card.workUnits} u`);

/** A task's short reference for the drawer's top line: its id is a UUID, so the first block stands in for it. */
export const taskRef = (taskId: string) => taskId.split('-')[0]!.toUpperCase();

// --- Filters -------------------------------------------------------------------------------------------------------

export interface WorkbenchFilters {
  q: string;
  business: string;
  type: string;
}

export function matchesFilters(card: WorkbenchCard, f: WorkbenchFilters): boolean {
  if (f.business && (card.businessName ?? '') !== f.business) return false;
  if (f.type && card.taskType !== f.type) return false;
  const q = f.q.trim().toLowerCase();
  if (!q) return true;
  return [card.title, card.businessName, card.projectName].some((text) => text?.toLowerCase().includes(q));
}

// --- The queue (List view) -----------------------------------------------------------------------------------------

export interface QueueGroup {
  id: 'today' | 'tomorrow' | 'later' | 'waiting';
  /** The head over the group: "Today · Wed 22 Oct". */
  heading: string;
  items: WorkbenchCard[];
}

const WAITING_ON_OTHERS: WorkbenchColumn[] = ['ready_for_qa', 'with_client'];

/** The end of the viewer's week (Sunday), as a day number. */
function endOfWeek(now: Date): number {
  const today = todayNumber(now);
  const weekday = new Date(today * DAY_MS).getUTCDay(); // 0 = Sunday
  return today + ((7 - weekday) % 7);
}

/**
 * Figma A05.7c: what to do today, tomorrow, later, and what is waiting on others (not the viewer's move).
 * A card is "Today" when it is due today or already late. The cards keep the API's order (soonest first) inside a group.
 */
export function groupQueue(cards: WorkbenchCard[], now: Date = new Date()): QueueGroup[] {
  const mine = cards.filter((card) => !WAITING_ON_OTHERS.includes(card.column));
  const waiting = cards.filter((card) => WAITING_ON_OTHERS.includes(card.column));
  const when = (card: WorkbenchCard) => dueWhen(card.deadline, now);
  const today = mine.filter((card) => when(card) === 'today' || when(card) === 'overdue');
  const tomorrow = mine.filter((card) => when(card) === 'tomorrow');
  const later = mine.filter((card) => when(card) === 'later' || when(card) === 'undated');

  const todayNo = todayNumber(now);
  const allThisWeek = later.every((card) => card.deadline && dayNumber(card.deadline) <= endOfWeek(now));
  const stamp = (offset: number) => dayText(new Date((todayNo + offset) * DAY_MS).toISOString());

  const groups: QueueGroup[] = [
    { id: 'today', heading: `Today · ${stamp(0)}`, items: today },
    { id: 'tomorrow', heading: `Tomorrow · ${stamp(1)}`, items: tomorrow },
    { id: 'later', heading: later.length > 0 && allThisWeek ? 'Later this week' : 'Later', items: later },
    { id: 'waiting', heading: 'Waiting on others · not your move', items: waiting },
  ];
  return groups.filter((group) => group.items.length > 0);
}

// --- The view the person last used ---------------------------------------------------------------------------------

export type WorkbenchView = 'board' | 'list';

const viewKey = (userId: string) => `mbm:my-work-view:${userId}`;

/** "Remember the last view per user": a convenience only, so every read and write is guarded. */
export function readView(userId: string | undefined): WorkbenchView {
  if (!userId) return 'board';
  try {
    return window.localStorage.getItem(viewKey(userId)) === 'list' ? 'list' : 'board';
  } catch {
    return 'board';
  }
}

export function saveView(userId: string | undefined, view: WorkbenchView) {
  if (!userId) return;
  try {
    window.localStorage.setItem(viewKey(userId), view);
  } catch {
    // Private windows and blocked storage: the page works the same, it just forgets.
  }
}
